import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Users,
  TrendingUp,
  Wallet,
  Sparkles,
  ArrowUpRight,
  RefreshCw,
} from "lucide-react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Kpi = ({ icon: Icon, label, value, sub, accent, link, count }) => (
  <div className="bb-card rounded-2xl p-5 relative overflow-hidden group">
    <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl opacity-50" style={{ background: accent || "#ff7a1a30" }}></div>
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/[0.04] border border-white/8">
          <Icon size={17} className="text-white/85" strokeWidth={1.8} />
        </div>
        {count != null && count > 0 && (
          <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#ff7a1a]/15 border border-[#ff7a1a]/30 text-[#ffae6a]">
            {count} pending
          </span>
        )}
      </div>
      <div className="text-[12px] text-white/55 mb-1">{label}</div>
      <div className="font-display text-[24px] lg:text-[26px] font-bold text-white tabular-nums leading-none">{value}</div>
      {sub && <div className="text-[11px] text-white/45 mt-1.5">{sub}</div>}
      {link && (
        <Link to={link} className="absolute top-5 right-5 inline-flex items-center justify-center w-7 h-7 rounded-lg border border-white/10 text-white/55 hover:text-white hover:border-white/25 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight size={13} />
        </Link>
      )}
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      // handled by interceptor on 401
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, []);

  return (
    <AdminLayout title="Dashboard" subtitle="Overview">
      <div className="flex items-center justify-between mb-5">
        <div className="text-[12px] text-white/55">
          Live overview of customer accounts, deposits and withdrawals.
        </div>
        <button
          onClick={() => {
            setRefreshing(true);
            load();
          }}
          className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-white/8 text-white/70 hover:text-white text-[12px]"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Kpi
          icon={ArrowDownToLine}
          label="Pending deposits"
          value={`$${fmt(stats?.pending_deposits?.total || 0)}`}
          sub={stats?.pending_deposits?.count ? "Action needed" : "All clear"}
          count={stats?.pending_deposits?.count}
          accent="#22c55e30"
          link="/admin/deposits"
        />
        <Kpi
          icon={ArrowUpFromLine}
          label="Pending withdrawals"
          value={`$${fmt(stats?.pending_withdrawals?.total || 0)}`}
          sub={stats?.pending_withdrawals?.count ? "Action needed" : "All clear"}
          count={stats?.pending_withdrawals?.count}
          accent="#ef444430"
          link="/admin/withdrawals"
        />
        <Kpi
          icon={Wallet}
          label="Customer balance"
          value={`$${fmt(stats?.customer_balance || 0)}`}
          sub={`Lifetime AUM $${fmt(stats?.aum || 0)}`}
          accent="#1c3df530"
        />
        <Kpi
          icon={Users}
          label="Total users"
          value={stats?.total_users || 0}
          sub={`${stats?.new_users_today || 0} new today`}
          accent="#a855f730"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 lg:gap-5">
        <div className="lg:col-span-2 bb-card rounded-2xl p-5 lg:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold text-[16px]">Recent activity</div>
            <div className="text-[11px] text-white/40">Last 20 actions</div>
          </div>
          {loading ? (
            <div className="py-10 text-center text-[12px] text-white/40">Loading...</div>
          ) : !stats?.recent_activity?.length ? (
            <div className="py-10 text-center text-[12px] text-white/40">No activity yet.</div>
          ) : (
            <div className="space-y-1">
              {stats.recent_activity.map((a) => (
                <div
                  key={a.id}
                  className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.03]"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/8 flex items-center justify-center shrink-0">
                    <Sparkles size={13} className="text-[#ffae6a]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-white">
                      <span className="font-medium capitalize">{a.action.replace(/_/g, " ")}</span>
                      <span className="text-white/45"> · {a.resource} #{(a.resource_id || "").slice(0, 12)}</span>
                    </div>
                    {a.note && <div className="text-[11px] text-white/45 truncate">{a.note}</div>}
                  </div>
                  <div className="text-[10px] text-white/40 shrink-0">
                    {new Date(a.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          <Link
            to="/admin/deposits"
            className="block bb-card rounded-2xl p-5 hover:border-emerald-500/30 transition-all group"
          >
            <ArrowDownToLine size={20} className="text-emerald-400 mb-3" />
            <div className="font-display font-semibold text-[16px] text-white mb-1">Process deposits</div>
            <div className="text-[12px] text-white/55">Approve customer deposits and credit balances.</div>
            <div className="mt-3 text-[12px] text-emerald-400 inline-flex items-center gap-1">
              Open queue <ArrowUpRight size={13} />
            </div>
          </Link>
          <Link
            to="/admin/withdrawals"
            className="block bb-card rounded-2xl p-5 hover:border-rose-500/30 transition-all group"
          >
            <ArrowUpFromLine size={20} className="text-rose-400 mb-3" />
            <div className="font-display font-semibold text-[16px] text-white mb-1">Process withdrawals</div>
            <div className="text-[12px] text-white/55">Mark withdrawals as paid after sending funds.</div>
            <div className="mt-3 text-[12px] text-rose-400 inline-flex items-center gap-1">
              Open queue <ArrowUpRight size={13} />
            </div>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
